import React, { useMemo } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import styled from 'styled-components'
import { Layout } from 'antd'
import {
  ShoppingCartOutlined,
  WalletOutlined,
  FileTextOutlined,
  DollarOutlined,
  PercentageOutlined
} from '@ant-design/icons'
import ValueAddedPurchasePage from './ValueAddedPurchasePage'
import ValueAddedRecordsPage from './ValueAddedRecordsPage'
import ValueAddedConsumptionPage from './ValueAddedConsumptionPage'
import ValueAddedRemainingBalancePage from './ValueAddedRemainingBalancePage'
import ValueAddedCommissionSettingsPage from './ValueAddedCommissionSettingsPage'
import PillTabs from '../components/PillTabs'
import { usePermission } from '../hooks/usePermission'

const { Content } = Layout

const ManagementContainer = styled(Layout)`
  min-height: calc(100vh - 120px);
  background: #fff;
`

const TabsHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  background: linear-gradient(180deg, #ffffff 0%, #ffffff 80%, transparent 100%);
  backdrop-filter: saturate(180%) blur(8px);
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f3f5;
`

const ContentWrapper = styled(Content)`
  padding: 0;
  background: transparent;
`

// 儲值金主色（紫色，與既有 ValueAdded 頁面保持一致）
const VALUE_ADDED_COLOR = '#7c3aed'

/**
 * 各 tab 對應權限：
 *  - 儲值購買：可執行儲值（balance:topup 或 finance:manage）
 *  - 剩餘儲值金、儲值記錄、儲值消費：查看財務（finance:view）
 *  - 抽成設定：店長以上（owner 或 finance:manage 或 *）
 *
 * 沒有權限就完全不顯示該 tab，避免使用者誤點進去看到 403 或空畫面。
 */
const buildTabs = ({ can, canAny, isBusinessOwner }) => {
  const tabs = []

  if (canAny(['balance:topup', 'finance:manage', 'finance:*'])) {
    tabs.push({
      key: 'purchase',
      label: '儲值購買',
      path: '/value-added/purchase',
      icon: <ShoppingCartOutlined />
    })
  }

  if (canAny(['finance:view', 'finance:manage', 'finance:*'])) {
    tabs.push({
      key: 'remaining-balance',
      label: '剩餘儲值金',
      path: '/value-added/remaining-balance',
      icon: <WalletOutlined />
    })
    tabs.push({
      key: 'records',
      label: '儲值記錄',
      path: '/value-added/records',
      icon: <FileTextOutlined />
    })
    tabs.push({
      key: 'consumption',
      label: '儲值消費',
      path: '/value-added/consumption',
      icon: <DollarOutlined />
    })
  }

  // 抽成設定：限店長以上（owner 或有 finance:manage 權限）
  if (isBusinessOwner() || can('finance:manage')) {
    tabs.push({
      key: 'commission',
      label: '抽成設定',
      path: '/value-added/commission',
      icon: <PercentageOutlined />
    })
  }

  return tabs
}

const ValueAddedManagementPage = () => {
  const { can, canAny, isBusinessOwner } = usePermission()

  const tabs = useMemo(
    () => buildTabs({ can, canAny, isBusinessOwner }),
    [can, canAny, isBusinessOwner]
  )

  // 第一條使用者實際看得到的 tab，作為 fallback redirect 目的地
  const fallbackPath = tabs[0]?.path || '/'

  return (
    <ManagementContainer>
      {tabs.length > 0 && (
        <TabsHeader>
          <PillTabs items={tabs} color={VALUE_ADDED_COLOR} />
        </TabsHeader>
      )}
      <ContentWrapper>
        <Routes>
          {/* 每條 route 也加雙重保護：權限不夠時 redirect 到第一個可見 tab */}
          <Route
            path='purchase'
            element={
              canAny(['balance:topup', 'finance:manage', 'finance:*']) ? (
                <ValueAddedPurchasePage />
              ) : (
                <Navigate to={fallbackPath} replace />
              )
            }
          />
          <Route
            path='records'
            element={
              canAny(['finance:view', 'finance:manage', 'finance:*']) ? (
                <ValueAddedRecordsPage />
              ) : (
                <Navigate to={fallbackPath} replace />
              )
            }
          />
          <Route
            path='consumption'
            element={
              canAny(['finance:view', 'finance:manage', 'finance:*']) ? (
                <ValueAddedConsumptionPage />
              ) : (
                <Navigate to={fallbackPath} replace />
              )
            }
          />
          <Route
            path='remaining-balance'
            element={
              canAny(['finance:view', 'finance:manage', 'finance:*']) ? (
                <ValueAddedRemainingBalancePage />
              ) : (
                <Navigate to={fallbackPath} replace />
              )
            }
          />
          <Route
            path='commission'
            element={
              isBusinessOwner() || can('finance:manage') ? (
                <ValueAddedCommissionSettingsPage />
              ) : (
                <Navigate to={fallbackPath} replace />
              )
            }
          />
          <Route path='*' element={<Navigate to={fallbackPath} replace />} />
        </Routes>
      </ContentWrapper>
    </ManagementContainer>
  )
}

export default ValueAddedManagementPage
